import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import {
    doc, getDoc, updateDoc,
    collection, query, where, orderBy, getDocs,
    addDoc, deleteDoc, deleteField, Timestamp,
} from 'firebase/firestore';
import dayjs from 'dayjs';
import { db, eventsCollectionName, eventScheduledBadgesCollectionName } from '../../../components/Firebase/Firebase';
import { useLoading } from '../../../components/context/LoadingContext';
import BadgePreview from './BadgePreview';
import BadgeForm from './BadgeForm';
import './EventBadgesPage.css';

const EventBadgesPage = () => {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useLoading();

    const [eventTitle, setEventTitle] = useState('');
    const [activeBadge, setActiveBadge] = useState(null);
    const [scheduledBadges, setScheduledBadges] = useState([]);

    const [showCurrentForm, setShowCurrentForm] = useState(false);
    const [showScheduledForm, setShowScheduledForm] = useState(false);
    const [editingScheduledId, setEditingScheduledId] = useState(null);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            // Fetch event document
            const eventRef = doc(db, eventsCollectionName, eventId);
            const eventSnap = await getDoc(eventRef);
            if (eventSnap.exists()) {
                const data = eventSnap.data();
                setEventTitle(data.title || '');
                setActiveBadge(data.activeBadge || null);
            }

            // Fetch scheduled badges
            const scheduledRef = collection(db, eventScheduledBadgesCollectionName);
            const scheduledBadgesQuery = query(
                scheduledRef,
                where('eventId', '==', eventId),
                orderBy('activateAt', 'asc')
            );
            const scheduledBadgesSnapshot = await getDocs(scheduledBadgesQuery);
            const badges = scheduledBadgesSnapshot.docs.map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() }));
            setScheduledBadges(badges);
        } catch (error) {
            console.error('Error fetching badge data:', error);
        }
        setIsLoading(false);
    }, [eventId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSaveCurrent = async (values) => {
        setIsLoading(true);
        try {
            const eventRef = doc(db, eventsCollectionName, eventId);
            await updateDoc(eventRef, {
                activeBadge: {
                    text: values.text,
                    textColor: values.textColor,
                    backgroundColor: values.backgroundColor,
                },
            });
            setActiveBadge({
                text: values.text,
                textColor: values.textColor,
                backgroundColor: values.backgroundColor,
            });
            setShowCurrentForm(false);
        } catch (error) {
            console.error('Error saving current badge:', error);
        }
        setIsLoading(false);
    };

    const handleRemoveCurrent = async () => {
        if (!window.confirm('Убрать текущую плашку с события?')) return;
        setIsLoading(true);
        try {
            const eventRef = doc(db, eventsCollectionName, eventId);
            await updateDoc(eventRef, { activeBadge: deleteField() });
            setActiveBadge(null);
        } catch (error) {
            console.error('Error removing badge:', error);
        }
        setIsLoading(false);
    };

    const handleSaveScheduled = async (values) => {
        setIsLoading(true);
        try {
            const badgeData = {
                eventId,
                text: values.text,
                textColor: values.textColor,
                backgroundColor: values.backgroundColor,
                activateAt: Timestamp.fromDate(dayjs(values.activateAt).toDate()),
            };

            if (editingScheduledId) {
                const docRef = doc(db, eventScheduledBadgesCollectionName, editingScheduledId);
                await updateDoc(docRef, badgeData);
            } else {
                await addDoc(collection(db, eventScheduledBadgesCollectionName), badgeData);
            }

            setShowScheduledForm(false);
            setEditingScheduledId(null);
            await fetchData();
        } catch (error) {
            console.error('Error saving scheduled badge:', error);
        }
        setIsLoading(false);
    };

    const handleDeleteScheduled = async (badgeId) => {
        if (!window.confirm('Удалить запланированную плашку?')) return;
        setIsLoading(true);
        try {
            await deleteDoc(doc(db, eventScheduledBadgesCollectionName, badgeId));
            setScheduledBadges((prev) => prev.filter((badge) => badge.id !== badgeId));
        } catch (error) {
            console.error('Error deleting scheduled badge:', error);
        }
        setIsLoading(false);
    };

    const handleEditScheduled = (badge) => {
        setEditingScheduledId(badge.id);
        setShowScheduledForm(true);
    };

    const getEditingInitialValues = () => {
        if (!editingScheduledId) return null;
        const badge = scheduledBadges.find((badge) => badge.id === editingScheduledId);
        if (!badge) return null;
        return {
            text: badge.text,
            textColor: badge.textColor,
            backgroundColor: badge.backgroundColor,
            activateAt: badge.activateAt?.toDate
                ? dayjs(badge.activateAt.toDate()).format('YYYY-MM-DDTHH:mm:ssZ')
                : '',
        };
    };

    const formatDate = (timestamp) => {
        if (!timestamp?.toDate) return '—';
        return dayjs(timestamp.toDate()).format('DD.MM.YYYY HH:mm');
    };

    return (
        <div className='right-content badges-page'>
            <div className='card shadow border-0'>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className='hd mb-0'>Плашки: {eventTitle}</h4>
                    <Button
                        variant="outlined"
                        onClick={() => navigate(`/events/edit/${eventId}`)}
                        sx={{ textTransform: 'none', color: '#fff', borderColor: '#666', '&:hover': { borderColor: '#888' } }}
                    >
                        Назад к событию
                    </Button>
                </div>

                {/* Section 1: Current badge */}
                <div className='section'>
                    <h5 className='section-title'>Текущая плашка</h5>
                    {activeBadge ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 10 }}>
                            <BadgePreview
                                text={activeBadge.text}
                                textColor={activeBadge.textColor}
                                backgroundColor={activeBadge.backgroundColor}
                            />
                            <div style={{ display: 'flex', gap: 8 }}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    sx={{ textTransform: 'none', backgroundColor: '#CEFC4C', color: '#000', '&:hover': { backgroundColor: '#b8e043' } }}
                                    onClick={() => setShowCurrentForm(true)}
                                >
                                    Изменить текущую
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleRemoveCurrent}
                                    sx={{ textTransform: 'none', color: '#f44', borderColor: '#f44', '&:hover': { borderColor: '#f66' } }}
                                >
                                    Убрать плашку
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <span style={{ color: '#888' }}>Нет активной плашки</span>
                            <div>
                                <Button
                                    variant="contained"
                                    size="small"
                                    sx={{ textTransform: 'none', backgroundColor: '#CEFC4C', color: '#000', '&:hover': { backgroundColor: '#b8e043' } }}
                                    onClick={() => setShowCurrentForm(true)}
                                >
                                    Установить плашку
                                </Button>
                            </div>
                        </div>
                    )}
                    {showCurrentForm && (
                        <BadgeForm
                            initialValues={activeBadge}
                            showDatePicker={false}
                            onSave={handleSaveCurrent}
                            onCancel={() => setShowCurrentForm(false)}
                        />
                    )}
                </div>

                {/* Section 2: Scheduled badges */}
                <div className='section'>
                    <h5 className='section-title'>Запланированные плашки</h5>
                    {scheduledBadges.length > 0 ? (
                        <div className='scheduled-list'>
                            {scheduledBadges.map((badge) => (
                                <div key={badge.id} className='scheduled-item'>
                                    <BadgePreview
                                        text={badge.text}
                                        textColor={badge.textColor}
                                        backgroundColor={badge.backgroundColor}
                                    />
                                    <span className='scheduled-date'>
                                        {formatDate(badge.activateAt)}
                                    </span>
                                    <div className='scheduled-actions'>
                                        <Button
                                            size="small"
                                            onClick={() => handleEditScheduled(badge)}
                                            sx={{ textTransform: 'none', color: '#CEFC4C', fontSize: 12 }}
                                        >
                                            Редактировать
                                        </Button>
                                        <Button
                                            size="small"
                                            onClick={() => handleDeleteScheduled(badge.id)}
                                            sx={{ textTransform: 'none', color: '#f44', fontSize: 12 }}
                                        >
                                            Удалить
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <span style={{ color: '#888' }}>Нет запланированных плашек</span>
                    )}
                    <div style={{ marginTop: 12 }}>
                        <Button
                            variant="contained"
                            size="small"
                            sx={{ textTransform: 'none', backgroundColor: '#CEFC4C', color: '#000', '&:hover': { backgroundColor: '#b8e043' } }}
                            onClick={() => {
                                setEditingScheduledId(null);
                                setShowScheduledForm(true);
                            }}
                        >
                            Добавить запланированную
                        </Button>
                    </div>
                    {showScheduledForm && (
                        <BadgeForm
                            key={editingScheduledId || 'new'}
                            initialValues={getEditingInitialValues()}
                            showDatePicker={true}
                            onSave={handleSaveScheduled}
                            onCancel={() => {
                                setShowScheduledForm(false);
                                setEditingScheduledId(null);
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default EventBadgesPage;
